// Plan selection utilities for electricity plans

import type { ElectricityPlan } from '@/types';
import { logger } from '@/utils/logger';

export interface MergedElectricityPlan extends ElectricityPlan {
  totalCost: number;
}

/**
 * Selects the top 3 electricity plans based on:
 * 1. Cheapest plan overall
 * 2. Second cheapest from a different provider (prefer non-100% renewable)
 * 3. Cheapest 100% renewable from a different provider
 */
export function selectTopPlans(plans: MergedElectricityPlan[]): MergedElectricityPlan[] {
  if (plans.length === 0) return [];

  // Sort by total cost (ascending)
  const sortedByPrice = [...plans].sort((a, b) => a.totalCost - b.totalCost);

  const selectedPlans: MergedElectricityPlan[] = [];
  const usedProviders = new Set<string>();

  // 1. Cheapest plan overall
  if (sortedByPrice.length > 0) {
    const plan1 = sortedByPrice[0];
    selectedPlans.push(plan1);
    usedProviders.add(plan1.vendorName);
    logger.info({ area: 'plan-selection' }, `Plan 1 - Cheapest overall: ${plan1.vendorName} ${plan1.totalCost}`);
  }

  // 2. Second cheapest from DIFFERENT provider (prefer non-100% renewable for diversity)
  let plan2 = sortedByPrice.find(
    (p) => !usedProviders.has(p.vendorName) && (p.percentGreen || 0) < 100
  );
  if (!plan2) {
    // Fallback to any different provider
    plan2 = sortedByPrice.find((p) => !usedProviders.has(p.vendorName));
  }
  if (plan2) {
    selectedPlans.push(plan2);
    usedProviders.add(plan2.vendorName);
    logger.info({ area: 'plan-selection' }, `Plan 2 - Second cheapest: ${plan2.vendorName} ${plan2.totalCost}`);
  }

  // 3. Cheapest 100% renewable from DIFFERENT provider
  const renewablePlans = plans
    .filter((p) => p.percentGreen === 100 && !usedProviders.has(p.vendorName))
    .sort((a, b) => a.totalCost - b.totalCost);

  let plan3: MergedElectricityPlan | undefined;
  if (renewablePlans.length > 0) {
    plan3 = renewablePlans[0];
    logger.info({ area: 'plan-selection' }, `Plan 3 - Cheapest 100% renewable: ${plan3.vendorName} ${plan3.totalCost}`);
  } else {
    // Fallback to third cheapest from different provider
    plan3 = sortedByPrice.find((p) => !usedProviders.has(p.vendorName));
    if (plan3) {
      logger.info({ area: 'plan-selection' }, `Plan 3 - Third cheapest (no renewable): ${plan3.vendorName} ${plan3.totalCost}`);
    }
  }
  if (plan3) {
    selectedPlans.push(plan3);
    usedProviders.add(plan3.vendorName);
  }

  logger.info({ area: 'plan-selection' }, `Selected ${selectedPlans.length} plans`);
  return selectedPlans;
}
